import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';

import { ArrowsUpFromLine, Certificate, FileBox, PackageCheck, ReceiptText, Split } from '@repo/media/icons';

import { Button } from '@repo/components/button';
import { ButtonLink } from '@repo/components/button-link';
import { DataCard } from '@repo/components/data-card';
import { DialogTrigger, ModalDialog } from '@repo/components/dialog';

import { formatDateTime } from '@repo/utils/format-date';
import { isNotEmpty } from '@repo/utils/is-not-empty';
import { normalise } from '@repo/utils/normalise';
import { unique } from '@repo/utils/unique';
import { useNumberFormatter } from '@repo/utils/use-number-formatter';
import { kgToTonnes } from '@repo/utils/weight';

import { ContentHeader } from '~/features/content-header/content-header.tsx';
import { queryCargoById } from '~/services/cargo/cargo.ts';
import type { DetailedBatch } from '~/services/cargo/types.ts';

import { BatchDetails } from './-components/batch-details';
import { CargoSankeyDiagram } from './-components/cargo-sankey-diagram';
import { CargoStatusBadge } from './-components/cargo-status-badge.tsx';
import { batchTypeDisplayValues } from './-utils.ts';

export const Route = createFileRoute(
  '/project/$project/(cargoes)/cargoes/$cargo',
)({
  component: CargoDetails,
  loader: async ({ context: { queryClient }, params: { project, cargo } }) => {
    await queryClient.ensureQueryData(queryCargoById(project, cargo));
  },
});

function CargoDetails() {
  const numberFormatter = useNumberFormatter({ maximumFractionDigits: 3 });
  const projectId = Route.useParams().project;
  const cargoId = Route.useParams().cargo;
  const cargo = useSuspenseQuery(queryCargoById(projectId, cargoId)).data;
  const certifiedBatches = cargo.batches.filter(b => b.certificateId);
  const invoices = unique(cargo.batches.map(({ invoiceId }) => invoiceId).filter(isNotEmpty));

  return (
    <div className="@container flex flex-col items-start gap-6 pb-6">
      <div className="space-y-1 pb-4 w-full border-b border-b-secondary-200">
        <ContentHeader back={{ to: '..', from: Route.fullPath }}>
          <div className="flex justify-between items-center">
            {cargoId}
            <CargoStatusBadge status={cargo.status} />
          </div>
        </ContentHeader>
        <p>
          Last updated
          {' '}
          {formatDateTime(cargo.lastUpdatedAt)}
        </p>
        {cargo.voyageReferenceNo && (
          <p>
            Voyage:
            {' '}
            <span className="font-mono">{cargo.voyageReferenceNo}</span>
          </p>
        )}
      </div>

      <div className={twMerge(
        'grid gap-4 w-full',
        '@3xl:grid-cols-3',
      )}
      >
        {cargo.receivedQuantityKilograms === 0
          ? (
              <DataCard
                label="Planned load"
                value={numberFormatter.format(kgToTonnes(cargo.bindingQuantityKilograms))}
                unit="tCO2"
                icon={FileBox}
                size="lg"
              />
            )
          : (
              <DataCard
                label="Initial quantity"
                value={numberFormatter.format(kgToTonnes(cargo.receivedQuantityKilograms))}
                unit="tCO2"
                icon={FileBox}
                size="lg"
              />
            )}
        <DataCard
          label="Emissions"
          value={numberFormatter.format(kgToTonnes(cargo.totalEmissionsKilograms))}
          unit="tCO2"
          icon={ArrowsUpFromLine}
          size="lg"
        />
        <DataCard
          label="Stored"
          value={numberFormatter.format(cargo.totalStoredKilograms)}
          unit="tCO2"
          icon={PackageCheck}
          size="lg"
        />
      </div>

      <div className="grid @2xl:grid-cols-3 gap-4 w-full">
        <div className="space-y-4 @2xl:order-2">
          {cargo.receivedQuantityKilograms > 0 && (
            <DialogTrigger>
              <Button
                icon={{ component: <Split /> }}
                color="secondary"
                variant="soft"
                className="w-full"
              >
                View sankey diagram
              </Button>

              <ModalDialog
                title="Cargo sankey diagram"
                size="max"
              >
                <CargoSankeyDiagram cargo={cargo} />
              </ModalDialog>
            </DialogTrigger>
          )}
          {invoices.map(id => <InvoiceCard key={id} id={id} />)}
          {certifiedBatches.length > 0
            ? certifiedBatches.map(batch => batch.certificateId && (
                <CertificateCard
                  key={batch.id}
                  batch={batch}
                  batches={cargo.batches}
                />
              ))
            : <CertificatePlaceholder /> }
        </div>
        <div className="@2xl:col-span-2 space-y-4 @2xl:order-1">
          {cargo.batches.map(batch => (
            <BatchDetails
              key={batch.id}
              cargo={cargo}
              batch={batch}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const certificateCardStyles = tv({
  slots: {
    card: [
      '',
      'bg-gradient-to-br from-primary-500 to-primary-600',
    ],
    description: 'text-primary-100',
  },
  variants: {
    disabled: {
      true: {
        card: 'from-secondary-100 to-secondary-200 text-secondary-500',
        description: 'text-secondary-500',
      },
    },
  },
});

function CertificatePlaceholder() {
  const styles = certificateCardStyles({ disabled: true });
  return (
    <div
      className={twMerge('p-6 rounded-lg shadow-sm text-white', styles.card())}
    >
      <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
        <Certificate className="w-5 h-5" />
        Storage Certificate
      </h3>
      <p className={twMerge('text-sm', styles.description())}>
        No batches in this cargo have been certified yet. Certificates appear here when they are issued.
      </p>
    </div>
  );
}

function CertificateCard({ batch, batches: _batches }: { batch: DetailedBatch; batches: DetailedBatch[] }) {
  const batches = normalise(_batches);
  const [typeDisplayName] = batchTypeDisplayValues(batch.type);
  const hasCertificate = Boolean(batch.certificateId);

  const styles = certificateCardStyles({ disabled: !hasCertificate });

  return (
    <div
      className={twMerge('p-6 rounded-lg shadow-sm text-white', styles.card())}
    >
      <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
        <Certificate className="w-5 h-5" />
        Storage Certificate
        {' '}
        {batches.ids.indexOf(batch.id) + 1}
        {' '}
        /
        {' '}
        {batches.ids.length}
      </h3>
      <p className={twMerge('text-sm mb-4', styles.description())}>
        {typeDisplayName}
        {' '}
        batch
        {' '}
        {batch.type && `${batch.id.substring(0, 4)} `}
        has been permanently stored and certified
      </p>
      <ButtonLink
        from={Route.fullPath}
        to="../../certificates/$cert"
        params={{ cert: batch.id }}
        color="secondary"
        icon={{ component: <Certificate size={20} /> }}
      >
        View certificate
      </ButtonLink>
    </div>
  );
}

function InvoiceCard({ id }: { id: string }) {
  return (
    <div className="bg-linear-to-br from-secondary-500 to-secondary-600 p-6 rounded-lg shadow-sm text-secondary-contrast">
      <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
        <ReceiptText className="w-5 h-5" />
        Invoice
      </h3>
      <p className="text-sm text-secondary-100 mb-4">
        Invoice
        {' '}
        {id.substring(0, 4)}
        {' '}
        created for cargo
      </p>
      <ButtonLink
        from={Route.fullPath}
        to="../../invoices/$invoice"
        params={{ invoice: id }}
        color="secondary"
        icon={{ component: <Certificate size={20} /> }}
      >
        View invoice
      </ButtonLink>
    </div>
  );
}
